<template>
  <div class="auth-mask" :class="{ open: visible }" @click.self="close">
    <div class="auth-modal" role="dialog" aria-modal="true">
      <button class="auth-close" type="button" aria-label="关闭" @click="close">&times;</button>

      <div class="auth-tabs">
        <span class="auth-tab" :class="{ active: isLoginView }" @click="switchView('password')">登录</span>
        <span class="auth-tab" :class="{ active: view === 'register' }" @click="switchView('register')">注册</span>
      </div>

      <p class="auth-notice" v-if="notice">{{ notice }}</p>

      <!-- 登录方式切换：账号密码 / 验证码（沿用旧站两条登录链路） -->
      <div class="auth-switch" v-if="view === 'password' || view === 'code'">
        <span :class="{ active: view === 'password' }" @click="switchView('password')">账号密码登录</span>
        <span :class="{ active: view === 'code' }" @click="switchView('code')">验证码登录</span>
      </div>
      <div class="auth-switch" v-else-if="view === 'forget'">
        <span class="active">忘记密码</span>
      </div>

      <form class="auth-form" novalidate @submit.prevent="submit">
        <!-- 手机号 -->
        <div class="auth-field">
          <label>手机号</label>
          <input
            type="tel"
            maxlength="11"
            autocomplete="tel"
            placeholder="请输入手机号"
            v-model.trim="form.phone"
          />
        </div>

        <!-- 短信验证码 -->
        <div class="auth-field-row" v-if="view !== 'password'">
          <div class="auth-field">
            <label>短信验证码</label>
            <input
              type="text"
              maxlength="6"
              autocomplete="off"
              placeholder="请输入短信验证码"
              v-model.trim="form.code"
            />
          </div>
          <button class="auth-code-btn" type="button" :disabled="disabledClick" @click="getCode">
            {{ disabledClick ? '获取验证码(' + time + '秒)' : '获取验证码' }}
          </button>
        </div>

        <!-- 密码 -->
        <div class="auth-field relative" v-if="view !== 'code'">
          <label>密码</label>
          <input
            :type="seeTwo ? 'text' : 'password'"
            autocomplete="off"
            :placeholder="view === 'password' ? '请输入密码' : '请设置密码（8-20位数字与字母组合）'"
            v-model="form.password"
          />
          <svg class="auth-eye" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
               stroke-linecap="round" stroke-linejoin="round" @click="seeTwo = !seeTwo">
            <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z"></path>
            <circle cx="12" cy="12" r="2.6"></circle>
            <path v-if="seeTwo" d="M4 20 20 4"></path>
          </svg>
        </div>

        <!-- 确认密码（忘记密码） -->
        <div class="auth-field relative" v-if="view === 'forget'">
          <label>确认密码</label>
          <input
            :type="seeOne ? 'text' : 'password'"
            autocomplete="off"
            placeholder="请再次输入密码"
            v-model="form.password2"
          />
          <svg class="auth-eye" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
               stroke-linecap="round" stroke-linejoin="round" @click="seeOne = !seeOne">
            <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z"></path>
            <circle cx="12" cy="12" r="2.6"></circle>
            <path v-if="seeOne" d="M4 20 20 4"></path>
          </svg>
        </div>

        <!-- 企业信息（注册） -->
        <div class="auth-field" v-if="view === 'register'">
          <label>企业名称</label>
          <input type="text" autocomplete="off" placeholder="请输入企业名称全称" v-model.trim="form.company" />
        </div>
        <div class="auth-field" v-if="view === 'register'">
          <label>邮箱</label>
          <input type="text" autocomplete="off" placeholder="请输入邮箱地址" v-model.trim="form.email" />
        </div>

        <p class="auth-err">{{ err }}</p>

        <div class="auth-links" v-if="view === 'password'">
          <span></span>
          <a @click="switchView('forget')">忘记密码</a>
        </div>
        <div class="auth-links" v-if="view === 'forget'">
          <a @click="switchView('password')">返回登录</a>
          <span></span>
        </div>

        <label class="auth-agree" v-if="view !== 'forget'">
          <input type="checkbox" v-model="checked" />
          <span>
            我已阅读并同意
            <router-link target="_blank" to="/aboutUs/case2?name=用户协议&id=1">《用户协议》</router-link>
            和
            <router-link target="_blank" to="/aboutUs/case3?name=隐私政策&id=2">《隐私政策》</router-link>
          </span>
        </label>

        <button class="btn btn-primary auth-submit" type="submit" :disabled="loading">
          {{ submitText }}
        </button>
      </form>
    </div>
  </div>
</template>

<script>
/* 登录 / 注册 / 忘记密码弹窗
   视觉与交互形态参考 website-handoff/js/auth.js（双页签 + 行内提示 + Esc/遮罩关闭），
   但**提交逻辑完整沿用本项目原有实现**：接口、参数、请求头、返回判定、cookie 与 store 写入均未改动。
   原有链路：user/captcha/get · user/password/login · user/captcha/login · user/register · user/password/update
   注意：不要替换为 handoff 的 localStorage 演示账号（DSSAuth）。 */
import { authApi } from '@/api';

const PHONE_RE = /^1(3\d|4[5-9]|5[0-35-9]|6[567]|7[0-8]|8\d|9[0-35-9])\d{8}$/;
const CODE_TYPE = { register: 1, forget: 3 };

export default {
  name: 'AuthModal',
  data() {
    return {
      view: 'password', // password | code | register | forget
      form: {
        phone: '',
        code: '',
        password: '',
        password2: '',
        company: '',
        email: ''
      },
      checked: false,
      loading: false,
      seeOne: false,
      seeTwo: false,
      err: '',
      notice: '',
      time: 60,
      disabledClick: false
    };
  },
  computed: {
    visible() {
      return !!this.$store.state.showLogin;
    },
    isLoginView() {
      return this.view === 'password' || this.view === 'code' || this.view === 'forget';
    },
    submitText() {
      if (this.view === 'register') return '立即注册';
      if (this.view === 'forget') return '确认更改';
      return '登 录';
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetForm();
      } else {
        this.clearTimer();
      }
    }
  },
  mounted() {
    window.addEventListener('dts:auth-open', this.onExternalOpen);
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeDestroy() {
    window.removeEventListener('dts:auth-open', this.onExternalOpen);
    document.removeEventListener('keydown', this.onKeydown);
    this.clearTimer();
  },
  methods: {
    /* 供其他页面复用：window.dispatchEvent(new CustomEvent('dts:auth-open', { detail: { view, notice } })) */
    onExternalOpen(e) {
      const detail = (e && e.detail) || {};
      this.view = detail.view || 'password';
      this.notice = detail.notice || '';
      this.$store.commit('setShowLogin', 1);
    },
    onKeydown(e) {
      if (e.key === 'Escape' && this.visible) this.close();
    },
    close() {
      this.err = '';
      this.$store.commit('setShowLogin', 0);
    },
    switchView(view) {
      this.view = view;
      this.err = '';
    },
    resetForm() {
      this.form = { phone: '', code: '', password: '', password2: '', company: '', email: '' };
      this.checked = false;
      this.seeOne = false;
      this.seeTwo = false;
      this.err = '';
      this.loading = false;
      this.clearTimer();
      this.time = 60;
      this.disabledClick = false;
    },
    clearTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    /* 与原实现一致：仅前端校验，报错文案沿用旧站 */
    validate() {
      const f = this.form;
      if (!f.phone) return '请输入手机号';
      if (this.view !== 'password') {
        if (!f.code) return '验证码不能为空';
      }
      if (this.view !== 'code') {
        if (f.password === '') return '请输入密码';
        if (!/^(?![0-9]+$)(?![a-zA-Z]+$)[0-9A-Za-z]{8,20}$/.test(f.password)) {
          return '请输入8-20位数字与字母的组合';
        }
      }
      if (this.view === 'forget') {
        if (f.password2 === '') return '请再次输入密码';
        if (f.password2 !== f.password) return '两次输入密码不一致!';
      }
      if (this.view === 'register') {
        if (!f.company) return '请输入企业名称';
        if (!f.email) return '请输入邮箱地址';
      }
      if (this.view !== 'forget' && !this.checked) return '请阅读并勾选下方协议';
      return '';
    },
    getCode() {
      if (this.disabledClick) return;
      if (!PHONE_RE.test(this.form.phone)) {
        this.$message.error('手机号输入有误');
        return;
      }
      const params = {
        phone: this.form.phone,
        captchaType: CODE_TYPE[this.view] || 2
      };
      authApi.sendCaptcha(params)
        .then((res) => {
          if (res.state === 200) {
            this.clearTimer();
            this.disabledClick = true;
            this.time = 60;
            this.timer = setInterval(() => {
              this.time--;
              if (this.time <= 0) {
                this.clearTimer();
                this.disabledClick = false;
                this.time = 60;
              }
            }, 1000);
          } else {
            this.$message.error(res.message);
          }
        })
        .catch(err => {
          this.$message.error(err);
        });
    },
    submit() {
      if (!PHONE_RE.test(this.form.phone)) {
        this.$message.error('手机号输入有误');
        return;
      }
      const msg = this.validate();
      if (msg) {
        this.err = msg;
        return;
      }
      this.err = '';
      this.loading = true;

      let request = authApi.login;
      let params = {};
      if (this.view === 'password') {
        params = {
          phone: this.form.phone,
          password: this.form.password
        };
        request = authApi.login;
      } else if (this.view === 'code') {
        params = {
          phone: this.form.phone,
          captcha: this.form.code
        };
        request = authApi.loginByCaptcha;
      } else if (this.view === 'register') {
        params = {
          phone: this.form.phone,
          password: this.form.password,
          captcha: this.form.code,
          orgName: this.form.company,
          email: this.form.email
        };
        request = authApi.register;
      } else if (this.view === 'forget') {
        params = {
          phone: this.form.phone,
          password: this.form.password,
          captcha: this.form.code,
          confirmPassword: this.form.password2
        };
        request = authApi.resetPassword;
      }
      request(params)
        .then((res) => {
          if (res.state === 200) {
            if (this.view === 'password' || this.view === 'code') {
              this.$store.commit('setShowLogin', 0);
              this.solve(res.data);
              this.$cookies.set('user', res.data, { expires: 0.7 });
              this.$store.commit('setToken', res.data.token);
              window.location.reload();
            } else if (this.view === 'register' || this.view === 'forget') {
              if (this.view === 'register') {
                this.$message.success('注册完成');
              }
              this.resetForm();
              this.view = 'password';
            }
          } else {
            this.$message.error(res.message);
          }
          this.loading = false;
        })
        .catch(err => {
          this.loading = false;
          this.$message.error(err);
        });
    },
    solve(user) {
      this.$store.commit('setUserName', user.userName);
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/AuthModal.vue